'use client'

import NextLink from 'next/link'
import {useLocale} from 'next-intl'
import type {ComponentProps, FC} from 'react'
import React from 'react'

import {routing} from '../routing'

type LinkProps = ComponentProps<typeof NextLink> & {
  locale?: string
}

const NEXT_LINK_PROPS = [
  'legacyBehavior',
  'prefetch',
  'shallow',
  'scroll',
  'replace',
  'passHref',
] as const

const omitNextLinkProps = (props: Omit<LinkProps, 'href' | 'locale' | 'children'>) => {
  const result = {...props}
  NEXT_LINK_PROPS.forEach((key) => delete result[key])
  return result
}

const Link: FC<LinkProps> = ({href, locale, children, ...rest}) => {
  const currentLocale = useLocale()
  const activeLocale = locale ?? currentLocale

  if (typeof href === 'string' && href.includes('://')) {
    // absolute link, probably leading out of our site
    if (rest.legacyBehavior && React.isValidElement(children)) {
      return React.cloneElement(children as React.ReactElement<HTMLAnchorElement>, {
        href,
        target: '_blank',
        rel: 'noopener noreferrer',
      })
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...omitNextLinkProps(rest)}>
        {children}
      </a>
    )
  }

  // mailto: and tel: are not routes, so they get no locale prefix
  if (typeof href === 'string' && /^(mailto|tel):/.test(href)) {
    if (rest.legacyBehavior && React.isValidElement(children)) {
      return React.cloneElement(children as React.ReactElement<HTMLAnchorElement>, {href})
    }
    return (
      <a href={href} {...omitNextLinkProps(rest)}>
        {children}
      </a>
    )
  }

  const localizedHref =
    typeof href === 'string'
      ? `${activeLocale === routing.defaultLocale ? '' : '/' + activeLocale}${
          href.startsWith('/') ? href : `/${href}`
        }` // 'href' is plain URL
      : {
          ...href,
          pathname: `${activeLocale === routing.defaultLocale ? '' : '/' + activeLocale}${
            href.pathname?.startsWith('/') ? href.pathname : `/${href.pathname}`
          }`,
        } // 'href' is an object { pathname, query }

  return (
    <NextLink href={localizedHref} {...rest}>
      {children}
    </NextLink>
  )
}

export default Link
